import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import queryString from 'query-string'
import { registerUser, signupUser, completeRegistration } from '@actions/registration'
import { openErrorNotification } from '@components/Signup/SignupWizard/helpers'
import { selectRegistrationStatus } from '@reducers/registration/registrationSelectors'
import { selectRegistrationComplete, selectCurrentUserStatus } from '@reducers/currentUser/currentUserSelectors'
import { createTaxProfile } from '@reducers/taxProfiles/taxProfilesSlice'
import { selectW9FileUrl } from '@reducers/taxProfiles/taxProfilesSelectors'
import { fetchShellUserByToken } from '@reducers/shellUser/shellUserSlice'
import { ERROR, SUCCEEDED } from '@constants/fetchStatuses'
import { SIGNUP_SUCCESS } from '@constants/routes'
import {
  AMC,
  COMPANY,
  COMPANY_MEMBER,
  IN_PROGRESS,
  PROPERTY_DATA_COLLECTOR,
  SOLE_PROPRIETOR,
} from '@components/Signup/constants'
import useSignupAnalyticsTracker from '@components/Signup/SegmentAnalytics/useSignupAnalyticsTracker'
import useSignupPageView from '@components/Signup/SegmentAnalytics/useSignupPageView'
import CreateAccount from './CreateAccount'

const CreateAccountContainer = ({ userType, handleContinue, initialValues, shellUser, invitedBy = {} }) => {
  useSignupPageView('Create Account')
  const track = useSignupAnalyticsTracker()

  const isPropertyDataCollector = userType === PROPERTY_DATA_COLLECTOR

  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()
  const { t: token = '' } = queryString.parse(location.search)

  const [submitting, setSubmitting] = useState(false)
  const [isAppraiser, setIsAppraiser] = useState(false)
  const [termsModalVisible, setTermsModalVisible] = useState(false)
  const [agreed, setAgreed] = useState(false)
  const [passwordRequirementsMet, setPasswordRequirementsMet] = useState(false)
  const [validationErrors, setValidationErrors] = useState([])
  const [formValues, setFormValues] = useState({
    password: '',
    confirm_password: '',
  })
  const [singlePageFormCompleting, setSinglePageFormCompleting] = useState(false)

  const registrationStatus = useSelector(selectRegistrationStatus)
  const currentUserStatus = useSelector(selectCurrentUserStatus)
  const registrationComplete = useSelector(selectRegistrationComplete)
  const w9FileUrl = useSelector(selectW9FileUrl)

  // Handle registration completion
  useEffect(() => {
    if (registrationComplete) {
      if (isPropertyDataCollector) {
        // PDC users complete directly - set completing state and redirect
        setSinglePageFormCompleting(true)
        // Skip updateUser call since registration is already complete
      } else {
        // Non-PDC users go to next step
        handleContinue()
      }
    } else if (!registrationComplete && submitting && (registrationStatus === ERROR || currentUserStatus === ERROR)) {
      setSubmitting(false)
      setSinglePageFormCompleting(false)
      openErrorNotification()
    }
  }, [
    registrationStatus,
    handleContinue,
    setSubmitting,
    currentUserStatus,
    submitting,
    registrationComplete,
    isPropertyDataCollector,
  ])

  // Handle PDC completion - redirect to success page after completion
  useEffect(() => {
    if (singlePageFormCompleting && currentUserStatus === SUCCEEDED) {
      history.push({ pathname: SIGNUP_SUCCESS })
    } else if (singlePageFormCompleting && currentUserStatus === ERROR) {
      setSinglePageFormCompleting(false)
      openErrorNotification()
    }
  }, [singlePageFormCompleting, currentUserStatus, history])

  // Scroll error banner into view when validation errors appear
  useEffect(() => {
    if (validationErrors.length > 0) {
      const cardElement = document.querySelector('.step-header')
      if (cardElement) {
        cardElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        })
      }
    }
  }, [validationErrors])

  // Helper function to extract validation errors from form error
  const extractFormValidationErrors = (error) => {
    if (!error?.errorFields?.length) {
      return []
    }
    return error.errorFields.map((field) => ({
      field: field.name[0],
      message: field.errors[0],
    }))
  }

  // Helper function to handle errors consistently
  const handleErrors = (error) => {
    setSubmitting(false)
    setSinglePageFormCompleting(false)

    // Check if this is a form validation error
    const validationErrors = extractFormValidationErrors(error)
    if (validationErrors.length > 0) {
      // It's a validation error - just show the validation messages
      setValidationErrors(validationErrors)
    } else {
      // It's an API/network error - show notification banner
      openErrorNotification()
    }
  }

  const handleSubmit = async (formRef) => {
    try {
      // Clear any previous validation errors
      setValidationErrors([])

      const formData = await formRef.current.validateFields()
      const { appraiser_type, company_name, email, firstname, lastname, is_appraiser, password } = formData

      const isAppraiserValue = is_appraiser || userType === COMPANY_MEMBER || userType === SOLE_PROPRIETOR

      const userData = {
        has_company: userType === COMPANY || userType === SOLE_PROPRIETOR || userType === AMC,
        company_name,
        email,
        firstname,
        lastname,
        is_sole_proprietor: userType === SOLE_PROPRIETOR,
        password,
        username: email,
        terms_and_conditions: true,
        is_appraiser: isAppraiserValue,
        bypass_license: isAppraiserValue,
        signup_progress: IN_PROGRESS,
        appraiser_type,
      }

      const trackingPayload = {
        company_name,
        email,
        firstname,
        lastname,
        is_appraiser,
        appraiser_type,
      }

      track({
        eventName: 'Create Account Button Clicked',
        payload: trackingPayload,
      })
      setSubmitting(true)
      dispatch(registerUser(userData, true))
    } catch (error) {
      handleErrors(error)
    }
  }

  const handleSinglePageFormSubmit = async (formRef, credentials, coverage, coverageLevel) => {
    try {
      // Clear any previous validation errors
      setValidationErrors([])

      let formValidationErrors = []
      let formData = {}

      // Try to validate form fields first
      try {
        formData = await formRef.current.validateFields()
      } catch (error) {
        formValidationErrors = extractFormValidationErrors(error)
      }

      // Check coverage area validation
      const coverageValidationErrors =
        !coverage || coverage.length === 0 ? [{ field: 'coverage', message: 'Coverage area is required' }] : []

      // Combine all validation errors
      const allValidationErrors = [...formValidationErrors, ...coverageValidationErrors]

      if (allValidationErrors.length > 0) {
        setValidationErrors(allValidationErrors)
        return
      }

      setSubmitting(true)
      const {
        firstname,
        lastname,
        email,
        cell_phone,
        password,
        primaryStreetAddress,
        primaryCity,
        primaryState,
        primaryZipCode,
        startingStreetAddress,
        startingCity,
        startingState,
        startingZipCode,
        legal_name,
        disregarded_entity_name,
        address,
        city,
        state,
        zip_code,
        tax_classification,
        ssn,
        ein,
      } = formData

      // Build starting location object if fields are present
      const startingAddress = startingStreetAddress
        ? {
            address: startingStreetAddress,
            city: startingCity,
            state: startingState,
            zip_code: startingZipCode,
          }
        : null

      // Map tax information for later use
      const taxProfileData = {
        legal_name,
        disregarded_entity_name,
        business_address: {
          address,
          city,
          state,
          zip_code,
        },
        tax_classification,
        ssn,
        ein,
      }

      const userData = {
        user_type: PROPERTY_DATA_COLLECTOR, // TODO update this if we extend this to other user types
        username: email,
        password,
        email,
        firstname,
        lastname,
        coverage,
        coverage_level: coverageLevel,
        terms_and_conditions: true,
        phone: cell_phone,
        primary_address: {
          address: primaryStreetAddress,
          city: primaryCity,
          state: primaryState,
          zip_code: primaryZipCode,
        },
        starting_address: startingAddress,
        property_data_collector_licenses: credentials,
      }

      const trackingPayload = {
        email,
        firstname,
        lastname,
        user_type: PROPERTY_DATA_COLLECTOR,
        phone: cell_phone,
        coverage_areas: coverage ? coverage.length : 0,
      }

      track({
        eventName: 'Create Account Button Clicked',
        payload: trackingPayload,
      })

      // Chain the requests: signup user -> fetch updated shell user -> create tax profile -> complete registration
      const signupResult = await dispatch(signupUser(userData, false)) // Don't auto-complete registration yet

      // Refresh shell user so we have the company id after calling signupUser
      const shellUserResponse = await dispatch(fetchShellUserByToken(token))
      const shellUserCompanyId = shellUserResponse.payload?.data?.company

      // Get the created user ID from signup result
      const createdUserId = signupResult.data?.id

      if (shellUserCompanyId && createdUserId && w9FileUrl) {
        await dispatch(
          createTaxProfile({
            tax_profile: {
              start_date: new Date().toISOString(),
              end_date: null,
              legal_name: taxProfileData.legal_name,
              w9_file_url: w9FileUrl,
              company_id: shellUserCompanyId,
              created_by: createdUserId,
            },
            tax_profile_sensitive_data: {
              legal_name: taxProfileData.legal_name,
              disregarded_entity_name: taxProfileData.disregarded_entity_name,
              business_address: taxProfileData.business_address,
              tax_classification: taxProfileData.tax_classification,
              ssn: taxProfileData.ssn,
              ein: taxProfileData.ein,
            },
          }, shellUser.jwt),
        )
      } else {
        setSubmitting(false)
        openErrorNotification()
        return
      }

      // Now that all operations are complete, trigger the login and registration completion
      await dispatch(completeRegistration(signupResult.data, signupResult.args))
    } catch (error) {
      handleErrors(error)
    }
  }

  const setTermsAgreed = (value) => {
    const verbiage = value ? 'Agreed' : 'Disagreed'
    track({ eventName: `Sign Up Terms ${verbiage}` })
    setAgreed(value)
  }

  const handleAgree = () => {
    setTermsAgreed(true)
    setTermsModalVisible(false)
  }

  const handleDisagree = () => {
    setTermsAgreed(false)
    setTermsModalVisible(false)
  }

  const openTermsModal = () => {
    track({ eventName: 'Sign Up Terms Clicked' })
    setTermsModalVisible(true)
  }

  const handlePasswordRequirementsChange = (met) => {
    setPasswordRequirementsMet(met)
  }

  const handleFormValuesChange = (changedValues, allValues, formRef) => {
    // Handle clearing field errors
    const updatedFields = Object.keys(changedValues)
      .filter((name) => formRef.current.getFieldError(name).length)
      .map((name) => ({ name, errors: [] }))
    formRef.current.setFields(updatedFields)

    // Clear validation error summary when user starts typing
    if (validationErrors.length > 0) {
      setValidationErrors([])
    }

    // Update password form values
    setFormValues({
      password: allValues.password,
      confirm_password: allValues.confirm_password,
    })
  }

  const handleAppraiserChange = (value) => {
    setIsAppraiser(value)
  }

  // Password validation function
  const validatePassword = () => {
    return Promise.resolve()
  }

  return (
    <CreateAccount
      userType={userType}
      initialValues={initialValues}
      invitedBy={invitedBy}
      submitting={submitting}
      isAppraiser={isAppraiser}
      termsModalVisible={termsModalVisible}
      agreed={agreed}
      passwordRequirementsMet={passwordRequirementsMet}
      validationErrors={validationErrors}
      formValues={formValues}
      singlePageFormCompleting={singlePageFormCompleting}
      onSubmit={handleSubmit}
      onSinglePageFormSubmit={handleSinglePageFormSubmit}
      onAgree={handleAgree}
      onDisagree={handleDisagree}
      onOpenTermsModal={openTermsModal}
      onPasswordRequirementsChange={handlePasswordRequirementsChange}
      onFormValuesChange={handleFormValuesChange}
      onAppraiserChange={handleAppraiserChange}
      onClearValidationErrors={() => setValidationErrors([])}
      onSetTermsAgreed={setTermsAgreed}
      validatePassword={validatePassword}
      shellUser={shellUser}
    />
  )
}

export default CreateAccountContainer
